import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "../api";
import type { BriefSummary, ChatMessage } from "../types";

interface Props {
  currentBrief: BriefSummary | null;
  allBriefs: BriefSummary[];
}

export function ChatPanel({ currentBrief, allBriefs }: Props) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(
    () => localStorage.getItem("digest.conversationId"),
  );
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (conversationId) localStorage.setItem("digest.conversationId", conversationId);
  }, [conversationId]);

  async function send() {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    const history = messages
      .filter((m) => !m.error)
      .map((m) => ({ role: m.role, content: m.content }));
    const next: ChatMessage[] = [...messages, { role: "user", content: text }];
    setMessages([...next, { role: "assistant", content: "", pending: true }]);
    setSending(true);
    void api.postEvent(`Chat message sent (${text.length} chars).`);

    try {
      const res = await api.chat({
        messages: [...history, { role: "user", content: text }],
        currentBriefId: currentBrief?.id ?? null,
        otherBriefIds: allBriefs.map((b) => b.id).filter((id) => id !== currentBrief?.id),
        conversationId,
      });
      if (res.conversationId) setConversationId(res.conversationId);
      setMessages([
        ...next,
        { role: "assistant", content: res.reply || "_(empty response)_", citations: res.citations },
      ]);
    } catch (err) {
      void api.postEvent("Chat request failed.", "error", { message: (err as Error).message });
      setMessages([
        ...next,
        { role: "assistant", content: `⚠️ ${(err as Error).message}`, error: true },
      ]);
    } finally {
      setSending(false);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  }

  function clearChat() {
    setMessages([]);
    setConversationId(null);
    localStorage.removeItem("digest.conversationId");
  }

  return (
    <section className="panel chat-panel">
      <div className="panel-header">
        <h2>Chat</h2>
        <div className="chat-context">
          {currentBrief ? `grounded in ${currentBrief.date}` : "no brief selected"}
          {messages.length > 0 && (
            <button className="icon-btn" title="Clear conversation" onClick={clearChat}>
              🗑
            </button>
          )}
        </div>
      </div>
      <div className="panel-body chat-messages scroll" ref={scrollRef}>
        {messages.length === 0 && (
          <div className="notice muted">
            Ask anything about the briefs. The assistant is grounded in the open brief and can search the web.
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`chat-bubble ${m.role} ${m.error ? "error" : ""}`}>
            {m.pending ? (
              <span className="typing">
                <span></span>
                <span></span>
                <span></span>
              </span>
            ) : (
              <div className="markdown">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="chat-input">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ask about the briefs… (Enter to send, Shift+Enter for newline)"
          rows={2}
        />
        <button className="primary" onClick={() => void send()} disabled={sending || !input.trim()}>
          {sending ? "…" : "Send"}
        </button>
      </div>
    </section>
  );
}
